import {keepPreviousData, useQuery, useQueryClient} from '@github-ui/react-query'
import {testIdProps} from '@github-ui/test-id-props'
import {useDebounce} from '@github-ui/use-debounce'
import {LinkIcon, TableIcon} from '@primer/octicons-react'
import {Button, SelectPanel, type SelectPanelItemInput} from '@primer/react'
import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'

import {projectSuggestionsQueryKey, projectSuggestionsQueryOptions} from '../../queries/project-suggestions'
import type {ProjectSuggestion} from './types'
import {useUpsertProjectLinks} from './use-upsert-project-links'

interface LinkProjectDialogProps {
  /** Repo owner login. */
  owner: string
  /** Repo name. */
  repo: string
  /**
   * When `true`, the dialog asks for templates only (`filter=templates` on the
   * suggestions endpoint) and renders templates-flavored copy in its trigger and
   * title.
   */
  isTemplateView: boolean
}

interface LinkProjectDialogPanelProps extends LinkProjectDialogProps {
  anchorRef: RefObject<HTMLElement | null>
  onClose: () => void
}

const SEARCH_DEBOUNCE_MS = 500

type ProjectSelectItem = SelectPanelItemInput & {
  /** Stable identity assigned to `id`. We encode the project number into a string
   * so the SelectPanel can use it as a React key without leaking the raw number
   * onto the DOM as an unknown attribute. */
  id: string
}

const ITEM_ID_PREFIX = 'project-'

function buildItemId(projectNumber: number): string {
  return `${ITEM_ID_PREFIX}${projectNumber}`
}

function parseItemId(id: unknown): number | null {
  if (typeof id !== 'string' || !id.startsWith(ITEM_ID_PREFIX)) {
    return null
  }
  const parsed = Number(id.slice(ITEM_ID_PREFIX.length))
  return Number.isInteger(parsed) ? parsed : null
}

function buildItem(suggestion: ProjectSuggestion): ProjectSelectItem {
  return {
    id: buildItemId(suggestion.number),
    text: suggestion.name,
    description: suggestion.owner,
    descriptionVariant: 'block',
    leadingVisual: TableIcon,
  }
}

/**
 * A dialog for linking projects to a repository.
 *
 * - Recent suggestions on first open (`scope=recent`); search switches to
 *   `scope=search` once the user types non-empty text.
 * - Multi-select with the project's currently-linked state pre-checked.
 * - Submitting on close commits a single `PUT /projects/beta/upsert` with both
 *   `"on"` (link) and `""` (unlink) entries — Rails parity for the picker, which
 *   also supports both directions per submit.
 *
 * UX notes:
 *
 * - The search input debounces at 500ms (matches `ProjectSearchInput` in this
 *   package) to avoid hammering the suggestions endpoint per keystroke.
 * - On mutation error the dialog stays open and surfaces the server's flash
 *   message inline via `notice`. The `pendingSelection` map is preserved so the
 *   user can retry without losing their work.
 */
export function LinkProjectDialog({owner, repo, isTemplateView}: LinkProjectDialogProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const label = isTemplateView ? 'Link a template' : 'Link a project'

  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <>
      <Button
        ref={triggerRef}
        leadingVisual={LinkIcon}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        // We render our own trigger (renderAnchor={null}), so expose the
        // expanded state ourselves to satisfy WCAG 4.1.2.
        aria-expanded={open}
        {...testIdProps('link-project-trigger')}
      >
        {label}
      </Button>
      {open && (
        <LinkProjectDialogPanel
          owner={owner}
          repo={repo}
          isTemplateView={isTemplateView}
          anchorRef={triggerRef}
          onClose={handleClose}
        />
      )}
    </>
  )
}

/**
 * Controlled body of {@link LinkProjectDialog}; mount conditionally — closing
 * unmounts and resets search/selection. Use directly when the trigger lives in
 * more than one place so focus return can follow whichever opened it.
 */
export function LinkProjectDialogPanel({owner, repo, isTemplateView, anchorRef, onClose}: LinkProjectDialogPanelProps) {
  // Raw input value (mirrors what the user sees) and the debounced value used to
  // key the request. Keeping them separate prevents flicker between keystrokes.
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  // Keyed on project number — the server's stable identity for a memex project
  // within an owner. `selected: true` means "viewer wants this linked when they
  // submit", `false` means "viewer wants this unlinked".
  const [pendingSelection, setPendingSelection] = useState<Map<number, boolean>>(() => new Map())
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const trimmedQuery = debouncedQuery.trim()
  const scope = trimmedQuery.length > 0 ? 'search' : 'recent'

  const {data, isFetching, isError} = useQuery({
    ...projectSuggestionsQueryOptions({
      owner,
      repo,
      scope,
      query: debouncedQuery,
      filter: isTemplateView ? 'templates' : undefined,
    }),
    // The panel only mounts while open, so no `open` gate here. For
    // `scope: 'search'` still gate on a non-empty query so we don't issue a
    // blank-search 200-row request.
    enabled: scope === 'recent' || trimmedQuery.length > 0,
    // Avoid flashing an empty list between keystrokes while the next request resolves.
    placeholderData: keepPreviousData,
  })

  const items = useMemo<ProjectSelectItem[]>(() => (data?.projects ?? []).map(buildItem), [data])

  // Server's view of which projects are linked, keyed by project number. We diff
  // against this when the user toggles selection so the upsert payload only ever
  // includes entries that actually changed.
  const serverSelectedNumbers = useMemo<Set<number>>(
    () => new Set((data?.projects ?? []).filter(p => p.selected).map(p => p.number)),
    [data],
  )

  const selectedItems = useMemo<ProjectSelectItem[]>(
    () =>
      items.filter(item => {
        const projectNumber = parseItemId(item.id)
        if (projectNumber === null) return false
        const pending = pendingSelection.get(projectNumber)
        return pending ?? serverSelectedNumbers.has(projectNumber)
      }),
    [items, pendingSelection, serverSelectedNumbers],
  )

  const debouncedSetQuery = useDebounce(setDebouncedQuery, SEARCH_DEBOUNCE_MS)

  const handleFilterChange = useCallback(
    (next: string) => {
      setInputValue(next)
      debouncedSetQuery(next)
    },
    [debouncedSetQuery],
  )

  const handleSelectedChange = useCallback(
    (next: SelectPanelItemInput | SelectPanelItemInput[] | undefined) => {
      const list = Array.isArray(next) ? next : next ? [next] : []
      const nextSelectedNumbers = new Set(
        list.map(item => parseItemId((item as ProjectSelectItem).id)).filter((n): n is number => n !== null),
      )
      // Compute toggles relative to the server's view so we only send entries that
      // actually differ. This keeps the upsert payload minimal and matches the
      // Rails picker's behavior of only including changed items.
      setPendingSelection(prev => {
        const updated = new Map(prev)
        for (const item of items) {
          const projectNumber = parseItemId(item.id)
          if (projectNumber === null) continue
          const wasSelected = serverSelectedNumbers.has(projectNumber)
          const isSelected = nextSelectedNumbers.has(projectNumber)
          if (wasSelected === isSelected) {
            updated.delete(projectNumber)
          } else {
            updated.set(projectNumber, isSelected)
          }
        }
        return updated
      })
    },
    [items, serverSelectedNumbers],
  )

  const upsert = useUpsertProjectLinks({owner, repo})
  const queryClient = useQueryClient()

  const cancelInflight = useCallback(() => {
    debouncedSetQuery.cancel()
    // Drop any in-flight suggestions request so a stale response can't repopulate
    // the panel after the user closes it.
    void queryClient.cancelQueries({queryKey: projectSuggestionsQueryKey(owner, repo)})
  }, [debouncedSetQuery, owner, queryClient, repo])

  const closePanel = useCallback(() => {
    cancelInflight()
    onClose()
  }, [cancelInflight, onClose])

  const handleSubmit = useCallback(async () => {
    // Guard against double-submit: SelectPanel's footer Save button isn't
    // automatically disabled while our mutation is in flight, so a quick second
    // click could otherwise fire a duplicate PUT.
    if (upsert.isPending) return
    // Clear any error from a prior failed attempt before starting a new one so a
    // stale message doesn't render in the gap between retries.
    setErrorMessage(null)

    const links: number[] = []
    const unlinks: number[] = []
    for (const [number, selected] of pendingSelection) {
      if (selected) {
        links.push(number)
      } else {
        unlinks.push(number)
      }
    }

    try {
      // The hook short-circuits empty change sets without firing a request, so we
      // can call through unconditionally and let it resolve cleanly when nothing
      // is queued.
      await upsert.mutateAsync({links, unlinks})
      closePanel()
    } catch (error) {
      // Stay open and surface the server's flash message inline so the user can
      // adjust their selection (e.g. drop a project they don't have read access
      // to) and retry.
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    }
  }, [closePanel, pendingSelection, upsert])

  const handleOpenChange = useCallback(
    (
      next: boolean,
      gesture: 'anchor-click' | 'anchor-key-press' | 'click-outside' | 'escape' | 'selection' | 'cancel',
    ) => {
      // The panel is already open (it only mounts while open), so ignore the
      // open-request gesture.
      if (next) return
      // Only `'selection'` (Save click in the modal footer) commits pending
      // changes. Everything else (cancel button, Escape, click-outside, and any
      // future gesture introduced by SelectPanel) discards the user's pending
      // changes and closes — matches the Rails picker, which only persists when
      // the viewer clicks Submit.
      if (gesture === 'selection') {
        void handleSubmit()
        return
      }
      closePanel()
    },
    [closePanel, handleSubmit],
  )

  const label = isTemplateView ? 'Link a template' : 'Link a project'

  const message = useMemo(() => {
    if (isError) {
      return {
        variant: 'error' as const,
        title: 'Suggestions could not be loaded',
        body: 'Try again later.',
      }
    }
    if (!isFetching && items.length === 0) {
      const noun = isTemplateView ? 'templates' : 'projects'
      const hasQuery = trimmedQuery.length > 0
      return {
        variant: 'empty' as const,
        title: hasQuery ? `No ${noun} matched` : `No ${noun} found`,
        body: hasQuery ? 'Try a different search term.' : `No ${noun} are available to link.`,
      }
    }
    return undefined
  }, [isError, isFetching, items.length, isTemplateView, trimmedQuery])

  return (
    <SelectPanel
      title={label}
      subtitle={
        isTemplateView
          ? 'Link a template from the organization to this repository'
          : 'Link a project from the organization to this repository'
      }
      aria-label={label}
      variant="modal"
      anchorRef={anchorRef}
      renderAnchor={null}
      open
      onCancel={closePanel}
      onOpenChange={handleOpenChange}
      items={items}
      selected={selectedItems}
      onSelectedChange={handleSelectedChange}
      onFilterChange={handleFilterChange}
      filterValue={inputValue}
      placeholderText={isTemplateView ? 'Filter templates' : 'Filter projects'}
      inputLabel={isTemplateView ? 'Filter templates' : 'Filter projects'}
      loading={isFetching && items.length === 0}
      message={message}
      notice={errorMessage ? {variant: 'error', text: errorMessage} : undefined}
      // `width` is a real `AnchoredOverlay` prop, but the styled-system lint
      // flags it on `SelectPanel` itself. Pass it through `overlayProps` to
      // satisfy the lint and match the precedent in `StarListMenu`.
      overlayProps={{width: 'medium'}}
    />
  )
}
